import { supabase } from '../../lib/supabaseClient';
import type { Tables } from '../../types/database.types';

export type Jurisdiction = Tables<'jurisdictions'>;

export const JURISDICTION_KINDS = ['tj', 'tcom', 'cph', 'ca', 'ta', 'caa', 'cass', 'ce', 'autre'] as const;
export type JurisdictionKind = (typeof JURISDICTION_KINDS)[number];

export const JURISDICTION_KIND_LABELS: Record<JurisdictionKind, string> = {
  tj: 'Tribunal judiciaire',
  tcom: 'Tribunal de commerce',
  cph: 'Conseil de prud’hommes',
  ca: 'Cour d’appel',
  ta: 'Tribunal administratif',
  caa: 'Cour administrative d’appel',
  cass: 'Cour de cassation',
  ce: 'Conseil d’État',
  autre: 'Autre',
};

export function jurisdictionKindLabel(kind: string): string {
  return JURISDICTION_KIND_LABELS[kind as JurisdictionKind] ?? JURISDICTION_KIND_LABELS.autre;
}

/** Type proposé d'après le nom saisi (même règle que la base, `guess_jurisdiction_kind`). */
export function guessJurisdictionKind(name: string): JurisdictionKind {
  const n = name.trim().toLowerCase();
  if (/^(tj|tribunal judiciaire)\b/.test(n)) return 'tj';
  if (/^(tc\b|t\.?\s*com\.?|tribunal de commerce|tae\b|tribunal des activit)/.test(n)) return 'tcom';
  if (/^(cph\b|conseil de prud)/.test(n)) return 'cph';
  if (/^(caa\b|cour administrative)/.test(n)) return 'caa';
  if (/^(ca\b|cour d['’]appel)/.test(n)) return 'ca';
  if (/^(ta\b|tribunal administratif)/.test(n)) return 'ta';
  if (/^(cass|cour de cassation)/.test(n)) return 'cass';
  if (/^(ce\b|conseil d['’][ée]tat)/.test(n)) return 'ce';
  return 'autre';
}

/** Clé de comparaison d'un nom : la base refuse deux juridictions de même clé. */
export function jurisdictionNameKey(name: string): string {
  return name.trim().toLowerCase();
}

const LONG_FORMS: [RegExp, string][] = [
  [/tribunal judiciaire/g, 'tj'],
  [/tribunal de commerce|tribunal des activites economiques|t com\b|tae\b/g, 'tc'],
  [/conseil de prud ?hommes|prud ?hommes/g, 'cph'],
  [/cour administrative d ?appel/g, 'caa'],
  [/cour d ?appel/g, 'ca'],
  [/tribunal administratif/g, 'ta'],
  [/cour de cassation/g, 'cass'],
  [/conseil d ?etat/g, 'ce'],
];

/**
 * Mots d'un nom, pour rapprocher deux écritures de la même juridiction :
 * sans accents ni ponctuation, formes longues ramenées au sigle, mots de liaison retirés.
 * « Tribunal judiciaire de Nantes », « T.J. Nantes » et « tj nantes » donnent tous [tj, nantes].
 */
export function jurisdictionSearchTokens(name: string): string[] {
  let n = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\b([a-z])\./g, '$1')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  for (const [long, short] of LONG_FORMS) n = n.replace(long, short);
  return n.split(' ').filter((word) => word && !['de', 'du', 'des', 'd', 'la', 'le', 'les', 'l'].includes(word));
}

/** Vrai si chaque mot saisi est le début d'un mot différent du nom de la juridiction. */
export function jurisdictionMatches(name: string, query: string): boolean {
  const words = jurisdictionSearchTokens(name);
  // Les mots saisis les plus longs choisissent d'abord : « ca caen » ne prend pas deux fois « caen ».
  const wanted = jurisdictionSearchTokens(query).sort((a, b) => b.length - a.length);
  for (const q of wanted) {
    const at = words.findIndex((word) => word.startsWith(q));
    if (at === -1) return false;
    words.splice(at, 1);
  }
  return true;
}

/** Vrai si deux noms désignent très probablement la même juridiction. */
export function sameJurisdictionName(a: string, b: string): boolean {
  return jurisdictionSearchTokens(a).join(' ') === jurisdictionSearchTokens(b).join(' ');
}

export interface JurisdictionWithUsage extends Jurisdiction {
  /** Nombre de dossiers (non supprimés) rattachés. */
  dossierCount: number;
}

export async function listJurisdictions(): Promise<Jurisdiction[]> {
  const { data, error } = await supabase.from('jurisdictions').select('*').order('name');
  if (error) throw new Error(`Impossible de charger les juridictions : ${error.message}`);
  return data ?? [];
}

/** Liste des réglages : toutes les juridictions avec leur nombre de dossiers visibles. */
export async function listJurisdictionsWithUsage(): Promise<JurisdictionWithUsage[]> {
  const [jurisdictions, usage] = await Promise.all([
    listJurisdictions(),
    supabase.from('dossiers').select('jurisdiction_id').is('deleted_at', null).not('jurisdiction_id', 'is', null),
  ]);
  if (usage.error) throw new Error(`Impossible de compter les dossiers : ${usage.error.message}`);
  const counts = new Map<string, number>();
  for (const row of usage.data ?? []) {
    if (row.jurisdiction_id) counts.set(row.jurisdiction_id, (counts.get(row.jurisdiction_id) ?? 0) + 1);
  }
  return jurisdictions.map((j) => ({ ...j, dossierCount: counts.get(j.id) ?? 0 }));
}

function friendly(error: { code?: string; message: string }): Error {
  if (error.code === '23505') return new Error('Une juridiction porte déjà ce nom.');
  return new Error(error.message);
}

export async function createJurisdiction(input: {
  name: string;
  kind: JurisdictionKind;
  createdBy?: string | null;
}): Promise<Jurisdiction> {
  const { data, error } = await supabase
    .from('jurisdictions')
    .insert({ name: input.name.trim(), kind: input.kind, created_by: input.createdBy ?? null })
    .select()
    .single();
  if (error) throw friendly(error);
  return data;
}

export async function updateJurisdiction(
  id: string,
  patch: { name?: string; kind?: JurisdictionKind; is_active?: boolean },
): Promise<Jurisdiction> {
  const { data, error } = await supabase
    .from('jurisdictions')
    .update({ ...patch, ...(patch.name !== undefined ? { name: patch.name.trim() } : {}) })
    .eq('id', id)
    .select()
    .single();
  if (error) throw friendly(error);
  return data;
}

/** Supprime une juridiction sans dossier. Avec des dossiers, il faut la fusionner. */
export async function deleteJurisdiction(id: string): Promise<void> {
  const { error } = await supabase.from('jurisdictions').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Passe tous les dossiers de `sourceId` sur `targetId`, puis supprime `sourceId`. Rend le nombre de dossiers déplacés. */
export async function mergeJurisdictions(sourceId: string, targetId: string): Promise<number> {
  const { data, error } = await supabase.rpc('merge_jurisdictions', { p_source: sourceId, p_target: targetId });
  if (error) throw new Error(error.message);
  return data ?? 0;
}
